import TextField from '@brands/Components/Inputs/MuiTextField/TextField';
import ProgressBar from '@brands/Components/ProgressBar/ProgressBar';
import { config } from '@brands/config/config';
import { Loader } from '@googlemaps/js-api-loader';
import { Button } from '@mui/material';
import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import styles from './styles.module.scss';
import { PharmacySubmit } from './types';

// Define types
interface FindPharmacyForm {
  pharmacy_name?: string;
  pharmacy_zipcode: string;
}

interface testPharmacy {
  name: string | null | undefined;
  address: string | null | undefined;
  lat: any;
  lng: any;
  phoneNumber: string;
}

interface FindPharmacyProps {
  setFindPharmacy: React.Dispatch<React.SetStateAction<boolean>>;
  setAddPharmacy: React.Dispatch<React.SetStateAction<boolean>>;
  AddNewPharmacy: (newPharmacy: PharmacySubmit) => Promise<void>;
  isFromPatientIntake?: boolean;
}

const SearchForPharmacy: React.FC<FindPharmacyProps> = ({
  setFindPharmacy,
  setAddPharmacy,
  AddNewPharmacy,
  isFromPatientIntake = false,
}) => {
  const {
    register,
    formState: { errors },
    setValue,
    watch,
    clearErrors,
  } = useForm<FindPharmacyForm>({
    mode: 'onChange',
  });

  const [Pharmacies, setPharmacies] = useState<testPharmacy[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false); // State to track lazy loading

  const [selectedPharmacyId, setSelectedPharmacyId] = useState<string | null>(null);

  // Custom debounce hook
  const useDebounce = (value: string, delay: number): string => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);
      return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
  };

  // Watch form inputs
  const pharmacyName = watch('pharmacy_name', '');
  const zipCode = watch('pharmacy_zipcode', '');
  const debouncedPharmacyName = useDebounce(pharmacyName || '', 500);
  const debouncedZipCode = useDebounce(zipCode, 500);

  const [visibleCount, setVisibleCount] = useState(4);

  // Function to load more pharmacies
  const loadMorePharmacies = (): void => {
    if (loadingMore) return;
    setLoadingMore(true);
    setTimeout(() => {
      setVisibleCount((prev) => prev + 4);
      setLoadingMore(false);
    }, 500);
  };

  const fetchPharmacies = useCallback(async () => {
    if (!debouncedZipCode) return;
    setLoading(true);

    const loader = new Loader({
      apiKey: config.api.googleAPI,
      version: 'weekly',
      libraries: ['places'], // you can add 'geometry' later if desired
    });

    try {
      // Step 1: Geocode ZIP → lat/lng
      const geocodeResponse = await axios.get(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${debouncedZipCode}&key=${config.api.googleAPI}`,
      );
      const { lat: centerLat, lng: centerLng } = geocodeResponse.data.results[0].geometry.location;

      // Step 2: Load the new Places API
      await loader.load();
      const { Place } = google.maps.places;

      let results: google.maps.places.Place[] = [];

      if (debouncedPharmacyName) {
        const textRequest: google.maps.places.SearchByTextRequest = {
          fields: ['id', 'displayName', 'formattedAddress', 'location', 'internationalPhoneNumber'],
          textQuery: `${debouncedPharmacyName} pharmacy`,
          locationBias: {
            center: { lat: centerLat, lng: centerLng },
            radius: 32186, // 20 miles
          },
          rankPreference: google.maps.places.SearchByTextRankPreference.DISTANCE,
          maxResultCount: 10,
        };

        const textResponse = await Place.searchByText(textRequest);
        results = textResponse.places;
      } else {
        const nearbyRequest: google.maps.places.SearchNearbyRequest = {
          fields: ['id', 'displayName', 'formattedAddress', 'location', 'internationalPhoneNumber'],
          locationRestriction: {
            center: { lat: centerLat, lng: centerLng },
            radius: 32186, // 20 miles
          },
          includedPrimaryTypes: ['pharmacy'],
          rankPreference: google.maps.places.SearchNearbyRankPreference.DISTANCE,
          maxResultCount: 10,
        };

        const nearbyResponse = await Place.searchNearby(nearbyRequest);
        results = nearbyResponse.places;
      }

      // Helper to normalize LatLng objects
      const getLatLng = (loc: any) =>
        typeof loc.lat === 'function' ? { lat: loc.lat(), lng: loc.lng() } : { lat: loc?.lat ?? 0, lng: loc?.lng ?? 0 };

      // Haversine distance formula
      const getDistanceInMeters = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
        const toRad = (x: number) => (x * Math.PI) / 180;
        const R = 6371e3; // Earth radius in meters
        const dLat = toRad(lat2 - lat1);
        const dLng = toRad(lng2 - lng1);
        const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
      };

      // Extract the ZIP from the "STATE ZIP" segment (second-to-last comma part),
      // matching the same parsing handleSelectPharmacy uses below. A plain regex
      // over the whole address risks matching a street number instead of the ZIP.
      const getZipFromAddress = (address: string): string | null => {
        const parts = address.split(',').map((p) => p.trim());
        const stateZip = parts.length > 1 ? parts[parts.length - 2] : '';
        const zip = stateZip.split(/\s+/).pop();
        return zip && /^\d{5}$/.test(zip) ? zip : null;
      };

      // Map + calculate distance + sort
      const pharmacies = results
        .map((p) => {
          const { lat, lng } = getLatLng(p.location);
          const distanceFromCenter = getDistanceInMeters(lat, lng, centerLat, centerLng);
          return {
            name: p.displayName ?? 'Unknown',
            address: p.formattedAddress ?? 'No address provided',
            phoneNumber: p.internationalPhoneNumber ?? 'N/A',
            lat,
            lng,
            distanceFromCenter,
          };
        })
        .sort((a, b) => a.distanceFromCenter - b.distanceFromCenter); // nearest first

      // Only pharmacies in the exact ZIP code entered; fall back to the nearest
      // results if that ZIP has none, so the list isn't left empty.
      const pharmaciesInZip = pharmacies.filter((p) => getZipFromAddress(p.address) === debouncedZipCode);

      setPharmacies(pharmaciesInZip.length > 0 ? pharmaciesInZip : pharmacies);
    } catch (error) {
      console.error('Error fetching pharmacies:', error);
      setPharmacies([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedPharmacyName, debouncedZipCode]);

  // Trigger search when debounced values change
  useEffect(() => {
    fetchPharmacies();
  }, [debouncedPharmacyName, debouncedZipCode, fetchPharmacies]);
  const getStreet = (address: string): string => {
    if (!address) return '';
    const parts = address.split(',').map((p) => p.trim());
    if (parts.length <= 3) return parts.slice(0, -1).join(', ');
    return parts.slice(0, parts.length - 3).join(', ');
  };

  const getCityStateZip = (address: string): string => {
    if (!address) return '';
    const parts = address.split(',').map((p) => p.trim());
    const lastThree = parts.slice(-3);
    return lastThree.join(', ');
  };

  const handleSelectPharmacy = (pharmacy: testPharmacy): void => {
    if (!pharmacy.address) return;

    setSelectedPharmacyId(`${pharmacy.lat}-${pharmacy.lng}`);

    const parts = pharmacy.address.split(',').map((p) => p.trim());
    const street = parts.length > 3 ? parts.slice(0, parts.length - 3).join(', ') : parts[0] || '';
    const city = parts.length > 2 ? parts[parts.length - 3] : '';
    const stateZip = parts.length > 1 ? parts[parts.length - 2] : '';
    const [state = '', zip = ''] = stateZip.split(/\s+/);

    const newPharmacy: PharmacySubmit = {
      pharmacy_name: pharmacy.name ?? '',
      pharmacy_street_address: street,
      pharmacy_city: city,
      pharmacy_state: state,
      pharmacy_zip_code: zip,
      pharmacy_phone_number: pharmacy.phoneNumber ?? '',
    };

    AddNewPharmacy(newPharmacy);
  };

  return (
    <div className={`${styles.pharmacyContainer} ${isFromPatientIntake && styles.pharmacyContainerPatientIntake}`}>
      {!isFromPatientIntake && (
        <ProgressBar currentStep={2} totalSteps={4} onBack={() => setFindPharmacy(false)} hideStepNumbers />
      )}
      <h2 className={`fs-unmask ${isFromPatientIntake && styles.patientIntakeh2}`}>
        Find a pharmacy {isFromPatientIntake && <span className={styles.requiredClass}>*</span>}
      </h2>
      <div className={styles.findPharmacy}>
        <form className={`${isFromPatientIntake && styles.inputWrapDiv}`}>
          <div className={styles.inputWrap}>
            <TextField
              {...register('pharmacy_zipcode', { required: 'ZIP code is required' })}
              label="Search by zip code"
              data-testid="find-pharmacy-by-zipcode"
              errors={errors.pharmacy_zipcode ? errors : {}}
              name="pharmacy_zipcode"
              value={watch('pharmacy_zipcode')}
              onChange={(e: any) => {
                if (errors.pharmacy_zipcode) clearErrors('pharmacy_zipcode');
                setValue('pharmacy_zipcode', e.target.value);
              }}
              required
              inputClass={`${styles.profileInputClass} fs-exclude`}
              wrapperClassName={`fs-unmask ${styles.completeProfileClass} ${
                isFromPatientIntake && styles.completeProfileClassPatientIntake
              }`}
            />
          </div>
          <div className={styles.inputWrap}>
            <TextField
              {...register('pharmacy_name')}
              label="Pharmacy name (optional)"
              data-testid="find-pharmacy-by-name"
              errors={errors.pharmacy_name ? errors : {}}
              name="pharmacy_name"
              value={watch('pharmacy_name')}
              onChange={(e: any) => {
                if (errors.pharmacy_name) clearErrors('pharmacy_name');
                setValue('pharmacy_name', e.target.value);
              }}
              inputClass={`${styles.profileInputClass} fs-exclude`}
              wrapperClassName={`fs-unmask ${styles.completeProfileClass} ${
                isFromPatientIntake && styles.completeProfileClassPatientIntake
              }`}
            />
          </div>
        </form>

        <div className={styles.pharmacyResults}>
          {loading && <p>Loading...</p>}
          {!loading && Pharmacies.length === 0 && debouncedZipCode && <p>No pharmacies found.</p>}
          {!loading && Pharmacies.length > 0 && (
            <div className={styles.pharmacyContainerList}>
              {Pharmacies.slice(0, visibleCount).map((pharmacy, index) => (
                <div
                  key={`${pharmacy.lat}-${pharmacy.lng}-${index}`} // Unique enough for pharmacies
                  className={`fs-exclude ${styles.pharmacyInfo} ${
                    selectedPharmacyId === pharmacy.name ? styles.selected : ''
                  }`}
                  onClick={() => handleSelectPharmacy(pharmacy)}
                  data-testid={`found-pharmacy-${index}`}
                >
                  <div className={`fs-exclude ${styles.pharmacyInfoLeft}`} data-testid={`found-pharmacy-${index}-div`}>
                    {/* Pharmacy Name */}
                    <span className={styles.sectionContentRowDetailsName} data-testid={`found-pharmacy-${index}-name`}>
                      {pharmacy.name ?? 'Unknown Pharmacy'}
                    </span>

                    {/* Street */}
                    <span
                      className={styles.sectionContentRowDetailsAddress}
                      data-testid={`found-pharmacy-${index}-address`}
                    >
                      {getStreet(pharmacy.address ?? '')}
                    </span>

                    {/* City / State / ZIP */}
                    <span
                      className={styles.sectionContentRowDetailsAddress}
                      data-testid={`found-pharmacy-${index}-zipcode`}
                    >
                      {getCityStateZip(pharmacy.address ?? '')}
                    </span>

                    {/* Phone Number (if available) */}
                    {pharmacy.phoneNumber && (
                      <span
                        className={styles.sectionContentRowDetailsAddress}
                        data-testid={`found-pharmacy-${index}-phone`}
                      >
                        📞 {pharmacy.phoneNumber}
                      </span>
                    )}
                  </div>
                </div>
              ))}
              {visibleCount < Pharmacies.length && (
                <span className={styles.loadMore} onClick={loadMorePharmacies}>
                  Load More Pharmacies
                </span>
              )}
            </div>
          )}
          {loadingMore && <p>Loading more pharmacies...</p>}
        </div>
        <div className={styles.cantFindPharmacy}>
          Can’t find your pharmacy?
          <Button
            variant="text"
            className={styles.addMaually}
            onClick={() => {
              setFindPharmacy(false);
              setAddPharmacy(true);
            }}
            data-testid="find-pharmacy-add-manually"
          >
            Add it manually.
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SearchForPharmacy;
